import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Stethoscope, Video, Phone, Mail, MapPin, CheckCircle2, XCircle, Menu, X, ArrowLeft, MapPinned, Users } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import PhoneAuth from './PhoneAuth';
import UserAccount from './UserAccount';
import SerdiPayPayment from './SerdiPayPayment';
import VideoConsultation from './VideoConsultation';
import DoctorPortal, { DoctorLogin } from './DoctorPortal';
import IncomingCallModal from './IncomingCallModal';
import useIncomingCall from './useIncomingCall';
import { startAuthRefresh, isTokenExpired } from './utils/authRefresh';
import { formatDate } from './utils/formatDate';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [currentPage, setCurrentPage] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('page');
    return ['booking', 'my-account', 'doctor-login', 'admin-login'].includes(requested) ? requested : 'home';
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDoctor, setIsDoctor] = useState(false);
  const [doctorData, setDoctorData] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(
    () => new URLSearchParams(window.location.search).get('page') === 'login'
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingMode, setBookingMode] = useState(null); // 'presentiel' | 'tele-generaliste' | 'tele-specialiste'
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [services, setServices] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAppointmentId, setCurrentAppointmentId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [videoConsultation, setVideoConsultation] = useState(null); // For teleconsultation

  // Get auth token for incoming call checks
  const authToken = localStorage.getItem('auth_token');
  
  // Hook for incoming call notifications (poll every 3 seconds when authenticated)
  const { incomingCall, acceptCall, rejectCall } = useIncomingCall(
    isAuthenticated && !videoConsultation, // Don't poll when already in a call
    authToken,
    3000
  );

  // Handle accepting incoming call
  const handleAcceptIncomingCall = async () => {
    const call = await acceptCall();
    if (call) {
      // Navigate to video consultation
      setVideoConsultation({
        appointmentId: call.appointment_id,
        fromIncomingCall: true
      });
    }
  };

  // Handle rejecting incoming call
  const handleRejectIncomingCall = async () => {
    await rejectCall();
  };

  // Navigate to page and scroll to top
  const navigateTo = (page) => {
    setCurrentPage(page);
    setMobileMenuOpen(false);
  };

  // Always start at the top when changing page or booking step
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage, bookingStep]);

  // Check if user is already authenticated
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const user = localStorage.getItem('user');
    if (token && user && !isTokenExpired(token)) {
      setIsAuthenticated(true);
      setCurrentUser(JSON.parse(user));
    } else if (token && isTokenExpired(token)) {
      // Nettoyage silencieux d'une session expirée au chargement
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
    // Démarre le rafraîchissement silencieux du JWT (session prolongée)
    const stopAuthRefresh = startAuthRefresh();
    return () => { if (stopAuthRefresh) stopAuthRefresh(); };
  }, []);

  // Fetch services
  React.useEffect(() => {
    fetch(`${API_URL}/api/services`)
      .then(res => res.json())
      .then(data => setServices(data.services))
      .catch(err => console.error('Error fetching services:', err));
  }, []);

  // Fetch doctors
  React.useEffect(() => {
    fetch(`${API_URL}/api/doctors`)
      .then(res => res.json())
      .then(data => setDoctors(data.doctors))
      .catch(err => console.error('Error fetching doctors:', err));
  }, []);

  // Fetch availability when doctor and date selected
  React.useEffect(() => {
    if (selectedDoctor && selectedDate) {
      fetch(`${API_URL}/api/availability/${selectedDoctor.doctor_id}?date=${selectedDate}`)
        .then(res => res.json())
        .then(data => setAvailableSlots(data.slots))
        .catch(err => console.error('Error fetching availability:', err));
    }
  }, [selectedDoctor, selectedDate]);

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedDoctor(null);
    setBookingStep(2);
  };

  const normalizeText = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const getDoctorsForService = (service) => {
    if (!service) return doctors;
    const sname = normalizeText(service.name);
    if (sname.includes('teleconsult')) {
      if (sname.includes('general')) {
        return doctors.filter(d => normalizeText(d.specialty).includes('genera'));
      }
      if (sname.includes('special')) {
        return doctors.filter(d => !normalizeText(d.specialty).includes('genera'));
      }
      return doctors;
    }
    const rules = [
      { svc: ['genera'], doc: ['genera'] },
      { svc: ['chirurg'], doc: ['chirurg'] },
      { svc: ['pediatr'], doc: ['pediatr'] },
      { svc: ['gyneco', 'matern'], doc: ['gyneco', 'obstetr', 'matern'] },
      { svc: ['interne'], doc: ['interne', 'internist'] },
      { svc: ['cardio'], doc: ['cardio'] },
      { svc: ['dermato'], doc: ['dermato'] },
      { svc: ['laborato'], doc: ['laborato', 'biolog'] },
      { svc: ['imagerie', 'radiolog'], doc: ['imagerie', 'radiolog'] },
      { svc: ['urgence'], doc: ['urgence'] },
    ];
    const rule = rules.find(r => r.svc.some(k => sname.includes(k)));
    if (rule) {
      return doctors.filter(d => {
        const spec = normalizeText(d.specialty);
        return rule.doc.some(k => spec.includes(k));
      });
    }
    const tokens = sname.split(/[^a-z]+/).filter(w => w.length >= 5);
    return doctors.filter(d => {
      const spec = normalizeText(d.specialty);
      return tokens.some(t => spec.includes(t.slice(0, 6)));
    });
  };

  const handleDoctorSelect = (doctor) => {
    setSelectedDoctor(doctor);
    setBookingStep(3);
  };

  const handleDateTimeSelect = () => {
    if (selectedDate && selectedTime) {
      // Check if user is authenticated
      if (!isAuthenticated) {
        setShowAuthModal(true);
      } else {
        setBookingStep(4);
      }
    }
  };

  const handleSubmitBooking = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert('Session expirée. Veuillez vous reconnecter.');
        setShowAuthModal(true);
        setLoading(false);
        return;
      }
      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          service_id: selectedService.service_id,
          doctor_id: selectedDoctor.doctor_id,
          appointment_date: selectedDate,
          appointment_time: selectedTime,
          reason_for_visit: reasonForVisit,
          payment_method: 'mobile_money'
        })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setCurrentAppointmentId(data.appointment_id);
        setPaymentAmount(selectedService.price_cdf);
        setCurrentPage('payment');
      } else if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('auth_token');
        alert('Votre session a expiré. Veuillez vous reconnecter pour continuer.');
        setShowAuthModal(true);
      } else {
        // Show actual backend error to help diagnose
        const errorMsg = typeof data.detail === 'string'
          ? data.detail
          : Array.isArray(data.detail)
            ? data.detail.map(e => e.msg || e).join(', ')
            : 'Erreur inconnue';
        alert(`Erreur lors de la création du rendez-vous : ${errorMsg}`);
        console.error('Appointment creation error:', response.status, data);
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert(`Erreur de connexion : ${error.message || 'Vérifiez votre connexion internet.'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
    setShowAuthModal(false);
    // Redirect directly to account page after login
    setCurrentPage('my-account');
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentPage('home');
  };

  const handlePaymentSuccess = () => {
    setCurrentPage('confirmation');
    resetBooking();
  };

  const resetBooking = () => {
    setBookingStep(1);
    setBookingMode(null);
    setSelectedService(null);
    setSelectedDoctor(null);
    setSelectedDate('');
    setSelectedTime('');
    setReasonForVisit('');
  };

  const handleAdminLogin = async () => {
    // Secure admin login via API
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('admin_token', data.access_token);
        setIsAdmin(true);
        setCurrentPage('admin');
      } else {
        alert(data.detail || 'Mot de passe incorrect');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      alert('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAdmin(false);
    setAdminPassword('');
    setCurrentPage('home');
  };

  // Show auth modal
  if (showAuthModal) {
    return <PhoneAuth onAuthSuccess={handleAuthSuccess} onBack={() => setShowAuthModal(false)} />;
  }

  // Show incoming call modal (overlay on top of current page)
  const incomingCallModal = incomingCall ? (
    <IncomingCallModal
      call={incomingCall}
      onAccept={handleAcceptIncomingCall}
      onReject={handleRejectIncomingCall}
    />
  ) : null;

  // Show video consultation
  if (videoConsultation) {
    return (
      <VideoConsultation
        appointmentId={videoConsultation.appointmentId}
        userId={currentUser?.user_id || 'guest'}
        userName={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Patient'}
        userRole="patient"
        onEnd={() => {
          setVideoConsultation(null);
          setCurrentPage('my-account');
        }}
      />
    );
  }

  // Show user account if authenticated and viewing account
  if (isAuthenticated && currentPage === 'my-account') {
    return (
      <>
        {incomingCallModal}
        <UserAccount 
          user={currentUser} 
          onLogout={handleLogout} 
          onStartConsultation={(apt) => setVideoConsultation({ appointmentId: apt.appointment_id })} 
          onBack={() => setCurrentPage('home')}
          onBooking={() => { resetBooking(); setCurrentPage('booking'); }}
        />
        <div className="bottom-nav" data-testid="bottom-nav">
          <button onClick={() => setCurrentPage('home')} data-testid="bottomnav-home">
            <Stethoscope size={22} strokeWidth={1.5} />
            Accueil
          </button>
          <button onClick={() => { resetBooking(); setCurrentPage('booking'); }} data-testid="bottomnav-booking">
            <Calendar size={22} strokeWidth={1.5} />
            RDV
          </button>
          <button className="active" data-testid="bottomnav-account">
            <User size={22} strokeWidth={1.5} />
            Mon Compte
          </button>
          <button onClick={handleLogout} data-testid="bottomnav-logout">
            <X size={22} strokeWidth={1.5} />
            Quitter
          </button>
        </div>
      </>
    );
  }

  // Show admin dashboard if logged in as admin
  if (isAdmin && currentPage === 'admin') {
    return <AdminDashboard onLogout={handleAdminLogout} />;
  }

  // Show doctor portal
  if (isDoctor && currentPage === 'doctor') {
    return <DoctorPortal onLogout={() => { setIsDoctor(false); setDoctorData(null); setCurrentPage('home'); }} />;
  }

  // Show doctor login
  if (currentPage === 'doctor-login') {
    return (
      <DoctorLogin 
        onLoginSuccess={(doctor) => {
          setDoctorData(doctor);
          setIsDoctor(true);
          setCurrentPage('doctor');
        }}
        onBack={() => setCurrentPage('home')}
      />
    );
  }

  // Show payment page
  if (currentPage === 'payment') {
    // Build appointment data for payment component
    const appointmentData = {
      appointment_id: currentAppointmentId,
      patient_name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() : 'Patient'
    };
    
    return (
      <>
        {incomingCallModal}
        <div className="payment-page">
          <SerdiPayPayment
            appointment={appointmentData}
            service={selectedService}
            onPaymentComplete={handlePaymentSuccess}
            onCancel={() => navigateTo('booking')}
          />
        </div>
      </>
    );
  }

  return (
    <div className="App">
      {/* Incoming call modal overlay */}
      {incomingCallModal}
      {/* Navigation */}
      <nav className="navbar" data-testid="main-navbar">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo">
            <a
              href="/"
              className="nav-back-to-site"
              data-testid="nav-back-to-site"
              title="Retour au site officiel du Centre Médical la Trinité"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '0.85rem', marginRight: 10 }}
            >
              <ArrowLeft size={16} /> <span className="nav-back-label">Site officiel</span>
            </a>
            <img src="/cmt_logo.png" alt="Logo CMT" className="nav-logo-img" />
            <span>Centre Médical La Trinité</span>
          </div>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
          
          <div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
            <button 
              onClick={() => navigateTo('home')} 
              data-testid="nav-home"
              className={currentPage === 'home' ? 'active' : ''}
            >
              Accueil
            </button>
            <button 
              onClick={() => { resetBooking(); navigateTo('booking'); }} 
              data-testid="nav-booking"
              className={currentPage === 'booking' ? 'active' : ''}
            >
              Prendre RDV
            </button>
            {isAuthenticated ? (
              <button 
                onClick={() => navigateTo('my-account')} 
                data-testid="nav-account"
                className={currentPage === 'my-account' ? 'active' : ''}
              >
                Mon Compte
              </button>
            ) : (
              <button 
                onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }} 
                data-testid="nav-login"
                className="btn-connexion"
              >
                Connexion / Inscription
              </button>
            )}
            <button 
              onClick={() => navigateTo('doctor-login')} 
              data-testid="nav-doctor"
              className={`nav-secondary ${currentPage === 'doctor-login' ? 'active' : ''}`}
            >
              Médecin
            </button>
            <button 
              onClick={() => navigateTo('admin-login')} 
              data-testid="nav-admin"
              className={`nav-secondary ${currentPage === 'admin-login' ? 'active' : ''}`}
            >
              Admin
            </button>
          </div>
        </div>
      </nav>

      {/* Bottom Navigation (mobile Android) */}
      <div className="bottom-nav" data-testid="bottom-nav">
        <button
          onClick={() => navigateTo('home')}
          className={currentPage === 'home' ? 'active' : ''}
          data-testid="bottomnav-home"
        >
          <Stethoscope size={22} strokeWidth={1.5} />
          Accueil
        </button>
        <button
          onClick={() => { resetBooking(); navigateTo('booking'); }}
          className={currentPage === 'booking' ? 'active' : ''}
          data-testid="bottomnav-booking"
        >
          <Calendar size={22} strokeWidth={1.5} />
          RDV
        </button>
        {isAuthenticated ? (
          <button
            onClick={() => navigateTo('my-account')}
            className={currentPage === 'my-account' ? 'active' : ''}
            data-testid="bottomnav-account"
          >
            <User size={22} strokeWidth={1.5} />
            Mon Compte
          </button>
        ) : (
          <button
            onClick={() => setShowAuthModal(true)}
            data-testid="bottomnav-login"
          >
            <User size={22} strokeWidth={1.5} />
            Connexion
          </button>
        )}
        <button
          onClick={() => navigateTo('doctor-login')}
          className={currentPage === 'doctor-login' ? 'active' : ''}
          data-testid="bottomnav-doctor"
        >
          <Video size={22} strokeWidth={1.5} />
          Médecin
        </button>
      </div>

      {/* Home Page */}
      {currentPage === 'home' && (
        <>
          {/* Hero Section */}
          <section className="hero" data-testid="hero-section">
            <div className="hero-content">
              <span className="hero-badge">🏥 Centre Médical Certifié</span>
              <h1 className="hero-title" data-testid="hero-title">
                Votre Confiance, Notre Engagement
              </h1>
              <p className="hero-subtitle" data-testid="hero-subtitle">
                Des soins médicaux de qualité avec des spécialistes expérimentés à Matadi et Boma
              </p>
              <button 
                className="btn-primary hero-cta" 
                onClick={() => { resetBooking(); setCurrentPage('booking'); }}
                data-testid="hero-cta-button"
              >
                <Calendar size={20} />
                Prendre Rendez-vous
              </button>
              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-number">10+</span>
                  <span className="hero-stat-label">Années d'expérience</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-number">5000+</span>
                  <span className="hero-stat-label">Patients satisfaits</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-number">15+</span>
                  <span className="hero-stat-label">Médecins spécialistes</span>
                </div>
              </div>
            </div>
          </section>

          {/* Services Section */}
          <section className="services-section" data-testid="services-section">
            <div className="container">
              <h2 className="section-title" data-testid="services-title">Nos Services</h2>
              <p className="section-subtitle">Une gamme complète de soins médicaux pour toute la famille</p>
              <div className="services-grid">
                {services.map((service) => (
                  <div key={service.service_id} className="service-card" data-testid={`service-card-${service.service_id}`}>
                    <div className="service-icon">
                      <Stethoscope size={40} />
                    </div>
                    <h3>{service.name}</h3>
                    <p>{service.description}</p>
                    <div className="service-meta">
                      <span><Clock size={16} /> {service.duration_minutes} min</span>
                      <span className="service-price" data-testid={`service-price-${service.service_id}`}>
                        <strong>{Math.round(service.price_cdf).toLocaleString('fr-FR')} CDF</strong>
                        {service.price_usd ? (
                          <em style={{ display: 'block', fontSize: '0.78rem', color: '#64748b', fontStyle: 'normal', fontWeight: 500, marginTop: 2 }}>
                            ≈ {Number(service.price_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                          </em>
                        ) : null}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Doctors Section */}
          <section className="doctors-section" data-testid="doctors-section">
            <div className="container">
              <h2 className="section-title" data-testid="doctors-title">Notre Équipe Médicale</h2>
              <div className="doctors-grid">
                {doctors.map((doctor) => (
                  <div key={doctor.doctor_id} className="doctor-card" data-testid={`doctor-card-${doctor.doctor_id}`}>
                    <img
                      src={(!doctor.photo_url || /via\.placeholder|placehold\.co/i.test(doctor.photo_url)) ? '/doctor-placeholder.svg' : doctor.photo_url}
                      alt={doctor.name}
                      className="doctor-photo"
                      onError={(e) => { e.target.onerror = null; e.target.src = '/doctor-placeholder.svg'; }}
                    />
                    <h3>{doctor.name}</h3>
                    <p className="doctor-specialty">{doctor.specialty}</p>
                    <p className="doctor-bio">{doctor.bio}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="features-section" data-testid="features-section">
            <div className="container">
              <h2 className="section-title">Pourquoi Nous Choisir</h2>
              <div className="features-grid">
                <div className="feature-card" data-testid="feature-online-booking">
                  <Calendar size={48} />
                  <h3>Réservation en Ligne</h3>
                  <p>Prenez rendez-vous 24h/24 en quelques clics</p>
                </div>
                <div className="feature-card" data-testid="feature-teleconsultation">
                  <Video size={48} />
                  <h3>Téléconsultation</h3>
                  <p>Consultez nos médecins depuis chez vous</p>
                </div>
                <div className="feature-card" data-testid="feature-specialists">
                  <User size={48} />
                  <h3>Spécialistes Qualifiés</h3>
                  <p>Une équipe de médecins expérimentés</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="contact-section" data-testid="contact-section">
            <div className="container">
              <h2 className="section-title">Nous Contacter</h2>
              <p className="section-subtitle">Disponibles 24h/24, 7j/7 pour prendre soin de vous</p>
              <div className="contact-grid">
                <div className="contact-item" data-testid="contact-phone">
                  <Phone size={24} />
                  <div>
                    <h4>Téléphone</h4>
                    <p><a href="tel:+243840061625">+243 840 061 625</a></p>
                    <p><a href="tel:+243858114128">+243 858 114 128</a></p>
                  </div>
                </div>
                <div className="contact-item" data-testid="contact-email">
                  <Mail size={24} />
                  <div>
                    <h4>Email</h4>
                    <p><a href="mailto:contact@cmlatrinite-matadi.com">contact@cmlatrinite-matadi.com</a></p>
                  </div>
                </div>
                <div className="contact-item" data-testid="contact-address">
                  <MapPin size={24} />
                  <div>
                    <h4>Nos Sites</h4>
                    <p><strong>MATADI</strong> - 1175, Av. Nkala-nkala</p>
                    <p><strong>BOMA</strong> - 1019, Av. du Fleuve</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Booking Page */}
      {currentPage === 'booking' && (
        <div className="booking-page" data-testid="booking-page">
          <div className="container">
            <h1 className="page-title" data-testid="booking-title">Prendre Rendez-vous</h1>
            
            {/* Progress Bar */}
            <div className="progress-bar" data-testid="booking-progress">
              <div className={`progress-step ${bookingStep >= 1 ? 'active' : ''}`}>1. Service</div>
              <div className={`progress-step ${bookingStep >= 2 ? 'active' : ''}`}>2. Médecin</div>
              <div className={`progress-step ${bookingStep >= 3 ? 'active' : ''}`}>3. Date & Heure</div>
              <div className={`progress-step ${bookingStep >= 4 ? 'active' : ''}`}>4. Informations</div>
            </div>

            {/* Step 1: Select Service — précédé du choix du mode (présentiel / téléconsultation) */}
            {bookingStep === 1 && (
              <div className="booking-step" data-testid="booking-step-1">
                {/* --- Choix du mode de consultation --- */}
                {!bookingMode && (
                  <div data-testid="booking-mode-selector">
                    <h2 style={{ marginBottom: '0.5rem' }}>Comment souhaitez-vous consulter ?</h2>
                    <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                      Choisissez le mode de consultation qui vous convient.
                    </p>
                    <div className="booking-mode-grid">
                      <button
                        type="button"
                        className="booking-mode-card"
                        onClick={() => setBookingMode('presentiel')}
                        data-testid="mode-presentiel-btn"
                      >
                        <div className="booking-mode-icon presentiel">
                          <MapPinned size={38} />
                        </div>
                        <h3>Consultation en présentiel</h3>
                        <p>Consultez un médecin directement au Centre Médical la Trinité à Matadi.</p>
                        <span className="booking-mode-cta">Choisir <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} /></span>
                      </button>
                      <button
                        type="button"
                        className="booking-mode-card"
                        onClick={() => setBookingMode('teleconsultation')}
                        data-testid="mode-teleconsultation-btn"
                      >
                        <div className="booking-mode-icon teleconsultation">
                          <Video size={38} />
                        </div>
                        <h3>Téléconsultation vidéo</h3>
                        <p>Consultez un médecin à distance depuis chez vous en toute confidentialité.</p>
                        <span className="booking-mode-cta">Choisir <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} /></span>
                      </button>
                    </div>
                  </div>
                )}

                {/* --- Choix du sous-mode téléconsultation --- */}
                {bookingMode === 'teleconsultation' && (
                  <div data-testid="tele-submode-selector">
                    <button
                      type="button"
                      className="booking-back-btn"
                      onClick={() => setBookingMode(null)}
                      data-testid="mode-back-btn"
                    >
                      <ArrowLeft size={16} /> Retour
                    </button>
                    <h2 style={{ marginBottom: '0.5rem' }}>Quel type de téléconsultation ?</h2>
                    <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
                      Choisissez selon votre besoin.
                    </p>
                    <div className="booking-mode-grid">
                      <button
                        type="button"
                        className="booking-mode-card"
                        onClick={() => setBookingMode('tele-generaliste')}
                        data-testid="tele-generaliste-btn"
                      >
                        <div className="booking-mode-icon generaliste">
                          <Stethoscope size={38} />
                        </div>
                        <h3>Généraliste</h3>
                        <p>Consultation de médecine générale à distance — questions courantes, suivi, ordonnances.</p>
                        <span className="booking-mode-cta">Choisir <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} /></span>
                      </button>
                      <button
                        type="button"
                        className="booking-mode-card"
                        onClick={() => setBookingMode('tele-specialiste')}
                        data-testid="tele-specialiste-btn"
                      >
                        <div className="booking-mode-icon specialiste">
                          <Users size={38} />
                        </div>
                        <h3>Spécialiste</h3>
                        <p>Consultation avec un médecin spécialiste (pédiatrie, gynécologie, cardiologie…).</p>
                        <span className="booking-mode-cta">Choisir <ArrowLeft size={16} style={{ transform: 'rotate(180deg)' }} /></span>
                      </button>
                    </div>
                  </div>
                )}

                {/* --- Liste des services filtrée par mode --- */}
                {(bookingMode === 'presentiel' || bookingMode === 'tele-generaliste' || bookingMode === 'tele-specialiste') && (() => {
                  const filteredServices = services.filter(s => {
                    const sname = normalizeText(s.name);
                    const isTele = sname.includes('teleconsult');
                    if (bookingMode === 'presentiel') return !isTele;
                    if (bookingMode === 'tele-generaliste') return isTele && sname.includes('general');
                    if (bookingMode === 'tele-specialiste') return isTele && sname.includes('special');
                    return true;
                  });
                  const modeLabel = bookingMode === 'presentiel'
                    ? 'Consultation en présentiel'
                    : bookingMode === 'tele-generaliste'
                      ? 'Téléconsultation — Généraliste'
                      : 'Téléconsultation — Spécialiste';
                  return (
                    <div data-testid="booking-services-list">
                      <button
                        type="button"
                        className="booking-back-btn"
                        onClick={() => setBookingMode(bookingMode.startsWith('tele-') ? 'teleconsultation' : null)}
                        data-testid="services-back-btn"
                      >
                        <ArrowLeft size={16} /> Retour
                      </button>
                      <h2 style={{ marginBottom: '0.35rem' }}>{modeLabel}</h2>
                      <p style={{ color: '#64748b', marginBottom: '1.25rem' }}>Sélectionnez un service pour continuer.</p>
                      <div className="services-grid">
                        {filteredServices.map((service) => (
                          <div
                            key={service.service_id}
                            className={`service-card clickable ${selectedService?.service_id === service.service_id ? 'selected' : ''}`}
                            onClick={() => handleServiceSelect(service)}
                            data-testid={`select-service-${service.service_id}`}
                          >
                            <div className="service-icon">
                              {normalizeText(service.name).includes('teleconsult') ? <Video size={40} /> : <Stethoscope size={40} />}
                            </div>
                            <h3>{service.name}</h3>
                            <p>{service.description}</p>
                            <div className="service-meta">
                              <span><Clock size={16} /> {service.duration_minutes} min</span>
                              <span className="service-price" data-testid={`service-price-tele-${service.service_id}`}>
                                <strong>{Math.round(service.price_cdf).toLocaleString('fr-FR')} CDF</strong>
                                {service.price_usd ? (
                                  <em style={{ display: 'block', fontSize: '0.78rem', color: '#64748b', fontStyle: 'normal', fontWeight: 500, marginTop: 2 }}>
                                    ≈ {Number(service.price_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
                                  </em>
                                ) : null}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {filteredServices.length === 0 && (
                        <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
                          Aucun service disponible pour ce mode pour le moment.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Step 2: Select Doctor */}
            {bookingStep === 2 && (() => {
              const filteredDoctors = getDoctorsForService(selectedService);
              return (
              <div className="booking-step" data-testid="booking-step-2">
                <h2>Choisissez votre Médecin</h2>
                {selectedService && (
                  <p className="booking-service-hint" data-testid="booking-service-hint">
                    {normalizeText(selectedService.name).includes('teleconsult')
                      ? 'Tous nos médecins sont disponibles en téléconsultation'
                      : `Médecins disponibles pour : ${selectedService.name}`}
                  </p>
                )}
                {filteredDoctors.length === 0 ? (
                  <div className="no-doctor-message" data-testid="no-doctor-message">
                    <h3>Médecin indisponible</h3>
                    <p>Aucun médecin n'est actuellement disponible pour <strong>{selectedService?.name}</strong>.</p>
                    <p>Veuillez choisir un autre service ou nous contacter au <a href="tel:+243840061625">+243 840 061 625</a>.</p>
                  </div>
                ) : (
                <div className="doctors-grid">
                  {filteredDoctors.map((doctor) => (
                    <div 
                      key={doctor.doctor_id} 
                      className={`doctor-card clickable ${selectedDoctor?.doctor_id === doctor.doctor_id ? 'selected' : ''}`}
                      onClick={() => handleDoctorSelect(doctor)}
                      data-testid={`select-doctor-${doctor.doctor_id}`}
                      style={{ position: 'relative' }}
                    >
                      <img
                        src={(!doctor.photo_url || /via\.placeholder|placehold\.co/i.test(doctor.photo_url)) ? '/doctor-placeholder.svg' : doctor.photo_url}
                        alt={doctor.name}
                        className="doctor-photo"
                        onError={(e) => { e.target.onerror = null; e.target.src = '/doctor-placeholder.svg'; }}
                      />
                      <h3>{doctor.name}</h3>
                      <p className="doctor-specialty">{doctor.specialty}</p>
                      <p className="doctor-bio">{doctor.bio}</p>
                    </div>
                  ))}
                </div>
                )}
                <button 
                  className="btn-secondary" 
                  onClick={() => setBookingStep(1)}
                  data-testid="back-to-service"
                >
                  Retour
                </button>
              </div>
              );
            })()}

            {/* Step 3: Select Date & Time */}
            {bookingStep === 3 && (
              <div className="booking-step" data-testid="booking-step-3">
                <h2>Sélectionnez Date et Heure</h2>
                <div className="datetime-selector">
                  <div className="form-group">
                    <label>Date</label>
                    <input 
                      type="date" 
                      value={selectedDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      data-testid="select-date"
                    />
                  </div>
                  
                  {selectedDate && availableSlots.length > 0 && (
                    <div className="form-group">
                      <label>Heure</label>
                      <div className="time-slots" data-testid="time-slots">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            className={`time-slot ${selectedTime === slot.time ? 'selected' : ''} ${!slot.available ? 'disabled' : ''}`}
                            onClick={() => slot.available && setSelectedTime(slot.time)}
                            disabled={!slot.available}
                            data-testid={`time-slot-${slot.time}`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="button-group">
                  <button 
                    className="btn-secondary" 
                    onClick={() => setBookingStep(2)}
                    data-testid="back-to-doctor"
                  >
                    Retour
                  </button>
                  <button 
                    className="btn-primary" 
                    onClick={handleDateTimeSelect}
                    disabled={!selectedDate || !selectedTime}
                    data-testid="continue-to-info"
                  >
                    Continuer
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation & Payment */}
            {bookingStep === 4 && isAuthenticated && (
              <div className="booking-step" data-testid="booking-step-4">
                <h2>Confirmation</h2>
                <div className="booking-summary">
                  <div className="summary-card">
                    <h3>Résumé de votre rendez-vous</h3>
                    <div className="summary-item">
                      <strong>Service:</strong> {selectedService.name}
                    </div>
                    <div className="summary-item">
                      <strong>Médecin:</strong> {selectedDoctor.name}
                    </div>
                    <div className="summary-item">
                      <strong>Date:</strong> {formatDate(selectedDate)}
                    </div>
                    <div className="summary-item">
                      <strong>Heure:</strong> {selectedTime}
                    </div>
                    <div className="summary-item">
                      <strong>Montant:</strong> {Math.round(selectedService.price_cdf).toLocaleString('fr-FR')} CDF
                      {selectedService.price_usd ? (
                        <span style={{ display: 'inline-block', marginLeft: 8, fontSize: '0.85rem', color: '#64748b' }}>
                          (≈ {Number(selectedService.price_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD)
                        </span>
                      ) : null}
                      <div style={{ fontSize: '0.78rem', color: '#1565C0', marginTop: 4 }}>
                        💳 Paiement Mobile Money possible en <strong>USD</strong> ou <strong>CDF</strong>.
                      </div>
                    </div>
                    
                    <div className="form-group" style={{
                      marginTop: '2rem',
                      padding: '1.25rem',
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 100%)',
                      border: '2px solid #F59E0B',
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.12)'
                    }}>
                      <label style={{
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        color: '#92400E',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: '0.5rem'
                      }}>
                        ✍️ Raison de la Visite <span style={{ color: '#DC2626' }}>*</span>
                      </label>
                      <p style={{
                        fontSize: '0.83rem',
                        color: '#78350F',
                        margin: '0 0 0.75rem',
                        lineHeight: 1.4
                      }}>
                        Décrivez précisément votre motif afin que le médecin puisse préparer votre consultation efficacement.
                      </p>
                      <textarea 
                        value={reasonForVisit}
                        onChange={(e) => setReasonForVisit(e.target.value)}
                        rows="4"
                        required
                        data-testid="input-reason"
                        placeholder="Ex : Douleur au ventre depuis 3 jours, fièvre nocturne, examen de contrôle post-opératoire…"
                        style={{
                          width: '100%',
                          padding: '0.85rem',
                          border: '1.5px solid #F59E0B',
                          borderRadius: 8,
                          fontSize: '0.95rem',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          background: 'white',
                          outline: 'none',
                        }}
                        onFocus={(e) => { e.target.style.borderColor = '#D97706'; e.target.style.boxShadow = '0 0 0 3px rgba(245,158,11,0.25)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#F59E0B'; e.target.style.boxShadow = 'none'; }}
                      />
                      <p style={{
                        fontSize: '0.75rem',
                        color: '#92400E',
                        margin: '0.4rem 0 0',
                        fontStyle: 'italic'
                      }}>
                        {reasonForVisit.length > 0
                          ? `${reasonForVisit.length} caractères saisis`
                          : 'Champ obligatoire — minimum quelques mots'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="button-group">
                  <button 
                    className="btn-secondary" 
                    onClick={() => setBookingStep(3)}
                    data-testid="back-to-datetime"
                  >
                    Retour
                  </button>
                  <button 
                    className="btn-primary" 
                    onClick={handleSubmitBooking}
                    disabled={loading || !reasonForVisit}
                    data-testid="submit-booking"
                  >
                    {loading ? 'Traitement...' : 'Procéder au Paiement'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Page */}
      {currentPage === 'confirmation' && (
        <div className="confirmation-page" data-testid="confirmation-page">
          <div className="container">
            <div className="confirmation-card">
              <CheckCircle2 size={64} className="success-icon" />
              <h1>Rendez-vous Confirmé!</h1>
              <p>Votre paiement a été traité avec succès.</p>
              <p>Vous pouvez consulter vos rendez-vous dans "Mon Compte".</p>
              <div className="button-group" style={{marginTop: '2rem'}}>
                <button 
                  className="btn-secondary" 
                  onClick={() => setCurrentPage('my-account')}
                  data-testid="goto-account"
                >
                  Mon Compte
                </button>
                <button 
                  className="btn-primary" 
                  onClick={() => { setCurrentPage('home'); window.history.replaceState({}, '', '/'); }}
                  data-testid="back-to-home"
                >
                  Retour à l'Accueil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Login Page */}
      {currentPage === 'admin-login' && (
        <div className="admin-login-page" data-testid="admin-login-page">
          <div className="admin-login-card">
            <div className="login-header">
              <img src="/cmt_logo.png" alt="Logo CMT" style={{ height: '72px' }} />
              <h1>Administration</h1>
              <p>Centre Médical La Trinité</p>
            </div>
            <div className="login-form">
              <div className="form-group">
                <label>Mot de passe administrateur</label>
                <input 
                  type="password" 
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Entrez le mot de passe"
                  data-testid="admin-password-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                />
              </div>
              <button 
                className="btn-primary" 
                onClick={handleAdminLogin}
                disabled={!adminPassword}
                data-testid="admin-login-button"
              >
                Se Connecter
              </button>
              <button 
                className="btn-back" 
                onClick={() => setCurrentPage('home')}
              >
                ← Retour à l'accueil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-section">
            <div className="footer-logo">
              <Stethoscope size={28} />
              <span>Centre Médical La Trinité</span>
            </div>
            <p className="footer-description">
              Des soins médicaux de qualité pour toute la famille. 
              Notre équipe de spécialistes est à votre service 24h/24.
            </p>
            <div className="footer-social">
              <a href="https://www.facebook.com/profile.php?id=100064249498862" target="_blank" rel="noopener noreferrer" className="social-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Centre Médical La TRINITÉ/Matadi
              </a>
            </div>
          </div>
          
          <div className="footer-section">
            <h4>Nos Sites</h4>
            <ul>
              <li>
                <MapPin size={16} /> 
                <div>
                  <strong>MATADI</strong>
                  <span>1175, Avenue Nkala-nkala</span>
                  <span>Quartier Ville basse, Commune de Matadi</span>
                </div>
              </li>
              <li>
                <MapPin size={16} /> 
                <div>
                  <strong>BOMA</strong>
                  <span>1019, Avenue du Fleuve</span>
                  <span>Quartier Fisher, Commune de Nzadi</span>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>Contact</h4>
            <ul>
              <li><Phone size={16} /> <a href="tel:+243840061625">+243 840 061 625</a></li>
              <li><Phone size={16} /> <a href="tel:+243858114128">+243 858 114 128</a></li>
              <li><Mail size={16} /> <a href="mailto:contact@cmlatrinite-matadi.com">contact@cmlatrinite-matadi.com</a></li>
              <li><Clock size={16} /> <strong>24h/24 - Tous les jours</strong></li>
            </ul>
          </div>
          
          <div className="footer-section">
            <h4>Services</h4>
            <ul>
              <li>Consultation Générale</li>
              <li>Cardiologie</li>
              <li>Dermatologie</li>
              <li>Pédiatrie</li>
              <li>Gynécologie</li>
              <li>Téléconsultation</li>
            </ul>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>© 2025 Centre Médical La Trinité - Tous droits réservés</p>
          <p>République Démocratique du Congo</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
